import { Link as RouterLink } from 'react-router-dom';
import { isMetricDeltaVisible, isMetricVisible } from '@/features/shared/metricVisibility';
import { resolveEntityTags } from '@/features/shared/tags';
import { formatStatusLabel, getStatusTextColor, Inline, Link, Text, Tooltip } from '@/ui';
import { getStringFilterOperators } from '@/ui/composites/grid/gridOperators';
import { TableTextCell, TagsInlineCell } from '@/ui/composites/table';
import { buildMetricColumns, type MetricColumnKey } from '@/ui/composites/table/tableUtils';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { Box, type GridColDef, type GridRenderCellParams } from '@/ui/primitives';
import type { MetricThresholdGroup, TestExecutionStatus, TestListItemDto } from '@flowqa/shared';

type TestsColumnsOptions = {
  selectedProjectId: string | null;
  onProjectSelect: (projectId: string) => void;
  onStatusSelect?: (status: TestExecutionStatus | null) => void;
  thresholds: MetricThresholdGroup;
  resolveThresholds: (row: { projectId: string }) => MetricThresholdGroup | undefined;
  showSuiteLinks: boolean;
  showDeltas?: boolean;
  disableLinks?: boolean;
};

const columnFlex = {
  name: 1,
  suite: 1,
  latest: 0.5,
  lastExecuted: 0.5,
  passRate: 0.5,
  frequency: 0.5,
  avgDuration: 0.5,
  flakeScore: 0.5,
  mttf: 0.5,
  delta: 0.4,
  tags: 0.75,
};
const NUMERIC_TEXT_SX = {};

export const buildTestsColumns = ({
  selectedProjectId,
  onProjectSelect,
  onStatusSelect,
  thresholds,
  resolveThresholds,
  showSuiteLinks,
  showDeltas = true,
  disableLinks = false,
}: TestsColumnsOptions): Array<GridColDef<TestListItemDto>> => {
  const stringOperators = getStringFilterOperators();
  const deltasEnabled = showDeltas;
  const resolvedThresholds = (row: TestListItemDto): MetricThresholdGroup => {
    if (selectedProjectId) return thresholds;
    return resolveThresholds(row) ?? thresholds;
  };
  const metricKeys: MetricColumnKey[] = [
    'passRate',
    'frequency',
    'avgDuration',
    ...(isMetricVisible('flakeScore') ? (['flakeScore'] as const) : []),
    ...(isMetricVisible('mttf') ? (['mttf'] as const) : []),
  ];

  const nameColumn: GridColDef<TestListItemDto> = {
    field: 'name',
    headerName: 'Test',
    flex: columnFlex.name,
    align: 'left',
    headerAlign: 'left',
    filterOperators: stringOperators,
    renderCell: (params) => (
      <TableTextCell
        primary={{
          label: params.row.name,
          ...(disableLinks
            ? {}
            : {
                href: `/tests/${params.row.slug}`,
                onClick: () => {
                  onProjectSelect(params.row.projectId);
                },
              }),
        }}
      />
    ),
  };

  const suiteColumn: GridColDef<TestListItemDto> = {
    field: 'suiteName',
    headerName: 'Suite',
    flex: columnFlex.suite,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: true,
    renderCell: (params: GridRenderCellParams<TestListItemDto>) => {
      if (!showSuiteLinks) {
        return <TagsInlineCell tags={[]} />;
      }
      const suiteItems = params.row.suites?.items ?? [];
      const suiteIds = params.row.suites?.ids ?? [];
      const tags = suiteItems.map((suite) => suite.name);
      const suiteLinks: Array<{ label: string; href: string }> = [];
      suiteItems.forEach((suite) => {
        const href = `/suites/${suite.slug}`;
        if (suiteLinks.some((item) => item.href === href)) return;
        suiteLinks.push({ label: suite.name, href });
      });
      if (suiteLinks.length > 0) {
        return (
          <Box sx={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
            <Inline
              gap="xs"
              wrap="nowrap"
              align="center"
              sx={{ width: '100%', minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap' }}
            >
              {suiteLinks.map((suite, index) =>
                disableLinks ? (
                  <Text
                    key={suite.href}
                    role="body"
                    truncate
                    component="span"
                    sx={{ display: 'inline-block', minWidth: 0, maxWidth: '100%' }}
                  >
                    {index < suiteLinks.length - 1 ? `${suite.label},` : suite.label}
                  </Text>
                ) : (
                  <Text
                    key={suite.href}
                    role="body"
                    component="span"
                    sx={{ display: 'inline-block', minWidth: 0, maxWidth: '100%' }}
                  >
                    <Link
                      component={RouterLink}
                      to={suite.href}
                      underline="hover"
                      color="inherit"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      sx={{
                        display: 'inline-block',
                        minWidth: 0,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flexShrink: 1,
                      }}
                    >
                      {index < suiteLinks.length - 1 ? `${suite.label},` : suite.label}
                    </Link>
                  </Text>
                ),
              )}
            </Inline>
          </Box>
        );
      }
      const fallbackTags = tags.length > 0 ? tags : suiteIds;
      return <TagsInlineCell tags={fallbackTags} />;
    },
  };

  const latestSummaryColumn: GridColDef<TestListItemDto> = {
    field: 'latestExecutionStatus',
    headerName: 'Lastest Status',
    flex: columnFlex.latest,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: true,
    renderCell: (params: GridRenderCellParams<TestListItemDto>) => {
      const latestExecution = params.row.latestExecution ?? null;
      const status = latestExecution?.status ?? null;
      const isClickable = Boolean(status && onStatusSelect);
      const label = status ? formatStatusLabel(status) : 'Untested';
      const runNumber = latestExecution?.runNumber ?? null;
      const executionHref =
        runNumber !== null && params.row.slug
          ? `/tests/${params.row.slug}/runs/${runNumber}`
          : null;
      const labelNode =
        executionHref && !disableLinks ? (
          <Link
            component={RouterLink}
            to={executionHref}
            underline="hover"
            color="inherit"
            onClick={(event) => {
              event.stopPropagation();
              onProjectSelect(params.row.projectId);
            }}
          >
            <Text role="body" component="span" color="inherit">
              {label}
            </Text>
          </Link>
        ) : (
          label
        );
      if (isClickable && !executionHref) {
        return (
          <Box
            component="span"
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              onStatusSelect?.(status ?? null);
            }}
          >
            <Text
              role="body"
              component="span"
              sx={{
                color: (theme) => getStatusTextColor(theme, status),
              }}
              transform="uppercase"
            >
              {labelNode}
            </Text>
          </Box>
        );
      }
      return (
        <Text
          role="body"
          component="span"
          sx={{
            color: (theme) => getStatusTextColor(theme, status),
          }}
          transform="uppercase"
        >
          {labelNode}
        </Text>
      );
    },
  };

  const lastExecutedColumn: GridColDef<TestListItemDto, Date | string | null> = {
    field: 'latestExecutionTimestamp',
    headerName: 'Last Executed',
    flex: columnFlex.lastExecuted,
    align: 'right',
    headerAlign: 'right',
    filterable: false,
    valueGetter: (_value, row) => row.latestExecution?.timestamp ?? null,
    renderCell: (params: GridRenderCellParams<TestListItemDto, Date | string | null>) => {
      const timestamp = params.value ?? null;
      const label = timestamp ? formatRelativeTime(timestamp) : '-';
      const fullTimestamp = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
      return (
        <Tooltip
          title={fullTimestamp}
          disableHoverListener={!fullTimestamp}
          arrow
          slotProps={{
            tooltip: { sx: { fontSize: (theme) => theme.typography.body2.fontSize } },
          }}
        >
          <span>
            <Text role="body" component="span" sx={NUMERIC_TEXT_SX}>
              {label.length > 0 ? label : '-'}
            </Text>
          </span>
        </Tooltip>
      );
    },
  };

  const metricColumns = buildMetricColumns<TestListItemDto>({
    enabledKeys: metricKeys,
    includeDeltas: (key) => {
      if (!deltasEnabled) return false;
      if (key === 'flakeScore') return isMetricDeltaVisible('flakeScore');
      if (key === 'mttf') return isMetricDeltaVisible('mttf');
      return true;
    },
    fields: {
      passRate: 'metrics.passRate',
      frequency: 'metrics.frequencyPerDay',
      avgDuration: 'metrics.averageDurationMs',
      flakeScore: 'metrics.flakeScore',
      mttf: 'metrics.mttfSeconds',
    },
    deltaFields: {
      passRate: 'metrics.delta.passRate',
      frequency: 'metrics.delta.frequencyPerDay',
      avgDuration: 'metrics.delta.averageDurationMs',
      flakeScore: 'metrics.delta.flakeScore',
      mttf: 'metrics.delta.mttfSeconds',
    },
    flex: {
      passRate: columnFlex.passRate,
      frequency: columnFlex.frequency,
      avgDuration: columnFlex.avgDuration,
      flakeScore: columnFlex.flakeScore,
      mttf: columnFlex.mttf,
    },
    deltaFlex: {
      passRate: columnFlex.delta,
      frequency: columnFlex.delta,
      avgDuration: columnFlex.delta,
      flakeScore: columnFlex.delta,
      mttf: columnFlex.delta,
    },
    thresholds: thresholds,
    resolveThresholds: (row) => resolvedThresholds(row),
    align: 'right',
    headerAlign: 'right',
  });

  const columns: Array<GridColDef<TestListItemDto>> = [
    nameColumn,
    ...(showSuiteLinks ? [suiteColumn] : []),
    latestSummaryColumn,
    lastExecutedColumn,
    ...metricColumns,
    {
      field: 'tags',
      headerName: 'Tags',
      flex: columnFlex.tags,
      filterable: false,
      sortable: false,
      renderCell: (params) => <TagsInlineCell tags={resolveEntityTags(params.row)} />,
    },
  ];

  return columns;
};
